// ============================================================================
// PrerequisitePicker — multi-select prior events + free-text alternative.
// ============================================================================
// Sits inside EventForm. Selected events become rows in event_prerequisite;
// the free-text field stores "equivalent training or experience accepted"
// notes that don't map to specific past events.
//
// We exclude the current event from the list (no self-loop) and exclude
// archived/closed events implicitly (api.events already filters those).
// ============================================================================

import { useEffect, useState } from 'react';
import { api, type PublicEvent } from '../api';

interface Props {
  token: string;
  currentEventId?: number;
  selectedIds: number[];
  prereqText: string;
  onChange: (next: { selectedIds: number[]; prereqText: string }) => void;
  disabled?: boolean;
}

export function PrerequisitePicker({
  token, currentEventId, selectedIds, prereqText, onChange, disabled,
}: Props) {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.events({ includePast: true, token })
      .then((d) => {
        const list = (d.events ?? []).filter((e) => e.event_id !== currentEventId);
        // Sort by start date desc so most recent appears first
        list.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
        setEvents(list);
      })
      .catch((err) => setError(err?.message || 'Could not load events.'));
  }, [token, currentEventId]);

  const selected = new Set(selectedIds);

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ selectedIds: Array.from(next), prereqText });
  };

  const selectedEvents = events.filter((e) => selected.has(e.event_id));
  const unselectedEvents = events.filter((e) => !selected.has(e.event_id));

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Prerequisites (optional)</span>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 text-xs">{error}</div>}

      <div>
        <span className="block text-xs font-medium text-gray-600 mb-1">Required prior events</span>
        {selectedEvents.length === 0 ? (
          <p className="text-xs text-gray-500 italic">None selected. Pick from the list below.</p>
        ) : (
          <ul className="space-y-1 mb-2">
            {selectedEvents.map((e) => (
              <li key={e.event_id}
                className="flex items-center justify-between gap-2 bg-uif-green/10 border border-uif-green/30 rounded px-2 py-1 text-xs">
                <span className="truncate">
                  <span className="font-semibold">{e.title}</span>
                  <span className="text-gray-600"> · {new Date(e.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</span>
                </span>
                <button type="button" onClick={() => toggle(e.event_id)} disabled={disabled}
                  className="rounded text-uif-green-dark hover:bg-uif-green/20 px-1.5 disabled:opacity-50">×</button>
              </li>
            ))}
          </ul>
        )}
        <select
          value=""
          onChange={(e) => { if (e.target.value) toggle(Number(e.target.value)); }}
          disabled={disabled || unselectedEvents.length === 0}
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
          <option value="">— Add a prior event… —</option>
          {unselectedEvents.map((e) => (
            <option key={e.event_id} value={e.event_id}>
              {e.title} ({new Date(e.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })})
            </option>
          ))}
        </select>
      </div>

      <label className="block">
        <span className="block text-xs font-medium text-gray-600 mb-1">…or equivalent training / experience accepted</span>
        <textarea rows={2} value={prereqText}
          onChange={(e) => onChange({ selectedIds, prereqText: e.target.value })}
          disabled={disabled}
          placeholder="e.g. Prior food safety certification or 2+ years of post-harvest handling experience"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
      </label>
    </div>
  );
}
